/**
 * useImage Hook - TanStack Query版
 * 画像データの取得と管理
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef, useEffect } from 'react'
import { queryKeys, invalidationPatterns } from '../../../lib/queryKeys'
import { imageQueryOptions } from '../../../lib/queryClient'
import { useStore } from '../../../store'
import { logger } from '../../../lib/logger'

// 画像取得のレスポンス型
interface ImageData {
  id: string
  url: string
  blob: Blob
  size: number
  type: string
}

// Blob URLのライフサイクル管理
class BlobURLManager {
  private urls = new Map<string, string>()
  
  createURL(blob: Blob, key: string): string {
    // 既存のURLがあれば削除
    this.revokeURL(key)
    
    // 新しいURLを作成
    const url = URL.createObjectURL(blob)
    this.urls.set(key, url)
    return url
  }
  
  revokeURL(key: string): void {
    const url = this.urls.get(key)
    if (url) {
      URL.revokeObjectURL(url)
      this.urls.delete(key)
    }
  }
  
  revokeAll(): void {
    this.urls.forEach(url => URL.revokeObjectURL(url))
    this.urls.clear()
  }
}

// グローバルなBlob URLマネージャー
const blobURLManager = new BlobURLManager()

/**
 * 画像を取得するカスタムフック
 */
export function useImage(imageId: string | null) {
  const getImage = useStore(state => state.getImage)
  const urlRef = useRef<string | null>(null)
  
  const query = useQuery({
    queryKey: queryKeys.images.blob(imageId || ''),
    queryFn: async (): Promise<ImageData | null> => {
      if (!imageId) return null
      
      try {
        // 既存のストアから画像を取得（現在の実装を維持）
        const dataUrl = await getImage(imageId)
        if (!dataUrl) {
          throw new Error('Image not found')
        }
        
        // Data URLからBlobに変換
        const response = await fetch(dataUrl)
        const blob = await response.blob()
        
        // Blob URLを作成
        const url = blobURLManager.createURL(blob, imageId)
        urlRef.current = url
        
        return {
          id: imageId,
          url,
          blob,
          size: blob.size,
          type: blob.type || 'image/jpeg',
        }
      } catch (error) {
        logger.error('Failed to load image:', error)
        throw error
      }
    },
    enabled: !!imageId,
    ...imageQueryOptions,
  })
  
  // クリーンアップ - queryのデータが変わったときとアンマウント時
  useEffect(() => {
    const currentUrl = urlRef.current
    const currentId = imageId
    
    return () => {
      // アンマウント時にBlob URLをクリーンアップ
      if (currentUrl && currentId) {
        // 少し遅延させてから削除（遷移アニメーション用）
        setTimeout(() => {
          blobURLManager.revokeURL(currentId)
        }, 500)
      }
    }
  }, [query.data, imageId])
  
  return {
    data: query.data,
    url: query.data?.url || null,
    blob: query.data?.blob || null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * 画像をプリフェッチするフック
 */
export function usePrefetchImage() {
  const queryClient = useQueryClient()
  const getImage = useStore(state => state.getImage)
  
  const prefetchImage = async (imageId: string) => {
    await queryClient.prefetchQuery({
      queryKey: queryKeys.images.blob(imageId),
      queryFn: async () => {
        const dataUrl = await getImage(imageId)
        if (!dataUrl) return null
        
        const response = await fetch(dataUrl)
        const blob = await response.blob()
        const url = blobURLManager.createURL(blob, imageId)
        
        return {
          id: imageId,
          url,
          blob,
          size: blob.size,
          type: blob.type || 'image/jpeg',
        }
      },
      ...imageQueryOptions,
    })
  }
  
  return { prefetchImage }
}

/**
 * 画像をアップロードするフック
 */
export function useUploadImage() {
  const queryClient = useQueryClient()
  const addImageToSet = useStore(state => state.addImageToSet)
  
  return useMutation({
    mutationFn: async ({
      file,
      setId,
    }: {
      file: File
      setId: string
    }) => {
      // FileをData URLに変換（現在の実装を維持）
      return new Promise<{ id: string; size: number }>((resolve, reject) => {
        const reader = new FileReader()
        
        reader.onload = async () => {
          try {
            const dataUrl = reader.result as string
            
            // ストアに保存（セットIDが必要）
            await addImageToSet(setId, dataUrl)
            
            resolve({ id: `image-${Date.now()}`, size: file.size })
          } catch (error) {
            reject(error)
          }
        }
        
        reader.onerror = () => {
          reject(new Error('Failed to read file'))
        }
        
        reader.readAsDataURL(file)
      })
    },
    
    onSuccess: () => {
      // 画像リストを無効化して再取得
      void queryClient.invalidateQueries({
        queryKey: invalidationPatterns.afterImageUpload(),
      })
    },
    
    onError: (error) => {
      logger.error('Image upload failed:', error)
    },
  })
}

/**
 * 複数画像を一括アップロードするフック
 */
export function useBatchUploadImages() {
  const queryClient = useQueryClient()
  const addImageToSet = useStore(state => state.addImageToSet)
  
  return useMutation({
    mutationFn: async ({ files, setId }: { files: File[]; setId: string }) => {
      const results = await Promise.all(
        files.map(async (file, index) => {
          const reader = new FileReader()
          
          return new Promise<{ id: string; name: string; size: number }>(
            (resolve, reject) => {
              reader.onload = async () => {
                try {
                  const dataUrl = reader.result as string
                  
                  await addImageToSet(setId, dataUrl, index)
                  
                  resolve({
                    id: `image-batch-${Date.now()}-${index}`,
                    name: file.name,
                    size: file.size,
                  })
                } catch (error) {
                  reject(error)
                }
              }
              
              reader.onerror = () => {
                reject(new Error(`Failed to read file: ${file.name}`))
              }
              
              reader.readAsDataURL(file)
            }
          )
        })
      )
      
      return results
    },
    
    onSuccess: () => {
      // 画像リストを無効化
      void queryClient.invalidateQueries({
        queryKey: invalidationPatterns.afterImageUpload(),
      })
    },
  })
}

/**
 * 隣接画像をプリフェッチするフック（PlaybackPage用）
 */
export function usePrefetchAdjacentImages(
  currentImageId: string | null,
  adjacentImageIds: { prev?: string; next?: string }
) {
  const { prefetchImage } = usePrefetchImage()
  
  useEffect(() => {
    if (!currentImageId) return
    
    // 前後の画像をプリフェッチ
    const prefetchAdjacent = async () => {
      const promises = []
      
      if (adjacentImageIds.prev) {
        promises.push(prefetchImage(adjacentImageIds.prev))
      }
      
      if (adjacentImageIds.next) {
        promises.push(prefetchImage(adjacentImageIds.next))
      }
      
      await Promise.all(promises)
    }
    
    void prefetchAdjacent()
  }, [currentImageId, adjacentImageIds.prev, adjacentImageIds.next, prefetchImage])
}

// クリーンアップ用のエクスポート
export const cleanupBlobURLs = () => {
  blobURLManager.revokeAll()
}